import React from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { MessagesSquare, Tag, X, Star, Ban } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};
const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const TagChip = ({ tag, onRemove }) => (
  <span
    data-testid={`tag-chip-${tag}`}
    className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[11px] px-2 py-0.5"
  >
    <Tag className="w-2.5 h-2.5" /> {tag}
    {onRemove && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(tag); }}
        className="ml-0.5 hover:text-amber-100"
        aria-label={`Remove tag ${tag}`}
      >
        <X className="w-2.5 h-2.5" />
      </button>
    )}
  </span>
);

/**
 * Renders the customers data table. Pure presentational — parent owns state.
 */
const CustomersTable = ({
  customers, tagInputFor, setTagInputFor, tagDraft, setTagDraft,
  addTag, removeTag, onOpenThread, onSendReviewRequest, onToggleDNC,
}) => (
  <div className="overflow-x-auto rounded border border-slate-800">
    <table className="w-full text-sm">
      <thead className="bg-slate-950/40 text-gray-400 text-left">
        <tr>
          <th className="px-3 py-2 font-medium">Customer</th>
          <th className="px-3 py-2 font-medium">Status</th>
          <th className="px-3 py-2 font-medium text-right">Interactions</th>
          <th className="px-3 py-2 font-medium text-right">Revenue</th>
          <th className="px-3 py-2 font-medium">First seen</th>
          <th className="px-3 py-2 font-medium">Last seen</th>
          <th className="px-3 py-2 font-medium">Tags</th>
          <th className="px-3 py-2 font-medium text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        {customers.map((c) => (
          <tr key={c.email} className="border-t border-slate-800 hover:bg-slate-900/70 align-top" data-testid={`customer-row-${c.email}`}>
            <td className="px-3 py-3">
              <div className="text-white font-medium">{c.name || '—'}</div>
              <div className="text-xs text-red-400">{c.email}</div>
              {c.notes_excerpt && (
                <div className="text-[11px] text-gray-500 mt-1 italic line-clamp-1">📝 {c.notes_excerpt}</div>
              )}
            </td>
            <td className="px-3 py-3">
              {c.status === 'returning' ? (
                <Badge variant="secondary" className="bg-green-600/20 text-green-300 border-green-600/30">Returning</Badge>
              ) : (
                <Badge variant="secondary" className="bg-slate-700/60 text-gray-300 border-slate-600">Lead</Badge>
              )}
            </td>
            <td className="px-3 py-3 text-right">
              <div className="text-white font-bold tabular-nums">{c.total_interactions}</div>
              <div className="text-[10px] text-gray-500 whitespace-nowrap">
                {c.contact_count} contact{c.contact_count === 1 ? '' : 's'} · {c.quote_count} quote{c.quote_count === 1 ? '' : 's'}
                {c.estimate_count > 0 && (<> · {c.estimate_count} est.</>)}
              </div>
            </td>
            <td className="px-3 py-3 text-right">
              {c.total_revenue > 0 ? (
                <span className="text-green-300 font-bold tabular-nums" data-testid={`customer-revenue-${c.email}`}>{usd(c.total_revenue)}</span>
              ) : (
                <span className="text-gray-600 tabular-nums">—</span>
              )}
            </td>
            <td className="px-3 py-3 text-gray-300 whitespace-nowrap">{fmtDate(c.first_seen)}</td>
            <td className="px-3 py-3 text-gray-300 whitespace-nowrap">{fmtDate(c.last_seen)}</td>
            <td className="px-3 py-3">
              <div className="flex flex-wrap gap-1 items-center">
                {c.tags.map((t) => (
                  <TagChip key={t} tag={t} onRemove={(tg) => removeTag(c, tg)} />
                ))}
                {tagInputFor === c.email ? (
                  <span className="inline-flex items-center gap-1">
                    <Input
                      autoFocus
                      data-testid={`tag-input-${c.email}`}
                      value={tagDraft}
                      onChange={(e) => setTagDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addTag(c); }
                        if (e.key === 'Escape') { setTagInputFor(null); setTagDraft(''); }
                      }}
                      placeholder="VIP, returning…"
                      className="h-6 px-2 text-xs bg-slate-800 border-slate-700 text-white w-28"
                    />
                    <button
                      type="button"
                      data-testid={`tag-add-${c.email}`}
                      onClick={() => addTag(c)}
                      className="text-xs text-amber-300 hover:text-amber-100"
                    >Add</button>
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid={`tag-open-${c.email}`}
                    onClick={() => { setTagInputFor(c.email); setTagDraft(''); }}
                    className="text-[11px] text-gray-500 hover:text-gray-300 underline decoration-dotted"
                  >+ tag</button>
                )}
              </div>
            </td>
            <td className="px-3 py-3 text-right whitespace-nowrap">
              <div className="flex items-center justify-end gap-1">
                <Button
                  size="sm" variant="outline"
                  data-testid={`customer-open-${c.email}`}
                  onClick={() => onOpenThread(c.email)}
                  className="border-slate-700 text-white hover:bg-slate-800"
                >
                  <MessagesSquare className="w-3 h-3 mr-1" /> Open
                </Button>
                {onSendReviewRequest && !c.do_not_contact && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="icon" variant="outline"
                        data-testid={`customer-review-${c.email}`}
                        title={c.review_status === 'sent' ? 'Send reminder (max 1)' : c.review_status === 'reminder_sent' ? 'Reminder already sent' : 'Send Google review request'}
                        disabled={c.review_status === 'reminder_sent' || c.review_status === 'received'}
                        className="border-amber-800 text-amber-300 hover:bg-amber-950 w-8 h-8"
                      >
                        <Star className="w-3 h-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-slate-900 border-slate-800">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                          {c.review_status === 'sent' ? 'Send review reminder?' : 'Send Google review request?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          {c.review_status === 'sent'
                            ? <>This is a reminder — customer already got a request. Only one reminder is allowed.</>
                            : <>We&apos;ll send the Google review request template to <strong className="font-mono">{c.email}</strong>. It will be logged in Emails → History.</>}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`customer-review-confirm-${c.email}`} onClick={() => onSendReviewRequest(c)} className="bg-red-600 hover:bg-red-700 text-white">Send</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {onToggleDNC && (
                  <Button
                    size="icon" variant="outline"
                    data-testid={`customer-dnc-${c.email}`}
                    title={c.do_not_contact ? 'Do Not Contact ON — click to remove' : 'Mark as Do Not Contact'}
                    onClick={() => onToggleDNC(c)}
                    className={c.do_not_contact
                      ? 'border-red-700 bg-red-900/30 text-red-300 hover:bg-red-950 w-8 h-8'
                      : 'border-slate-700 text-gray-400 hover:bg-slate-800 w-8 h-8'}
                  >
                    <Ban className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default CustomersTable;
